// FTEDN Creator — Shipment Tracker (wired to API)
import { useEffect, useState } from 'react';
import { apiClient } from '../../hooks/useApi';
import type { BetaApplication } from '../../stores/beta.store';
import { buildShipmentTimeline, type ShipmentStep } from '../BetaProgram/beta.helpers';
import { Skeleton } from '../../components/ui/Skeleton';
import './ShipmentTracker.css';

interface ShipmentTrackerProps {
  programId?: string;
  application?: BetaApplication | null;
}

export function ShipmentTracker({ programId, application }: ShipmentTrackerProps) {
  const [steps, setSteps] = useState<ShipmentStep[]>(() => buildShipmentTimeline(application?.shipment ?? null));
  const [shipment, setShipment] = useState<Record<string, unknown> | null>(application?.shipment ?? null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!programId) return;
    let cancelled = false;
    const shipmentKey = programId;
    const fallbackShipment = application?.shipment ?? null;
    setShipment(fallbackShipment);
    setSteps(buildShipmentTimeline(fallbackShipment));

    async function loadShipment() {
      setLoading(!fallbackShipment);
      try {
        try {
          const canonical = await apiClient.get(`/products/${encodeURIComponent(shipmentKey)}/shipments`);
          if (cancelled) return;
          if (canonical.success && Array.isArray(canonical.data) && canonical.data.length > 0) {
            const nextShipment = canonical.data[0] as Record<string, unknown>;
            setShipment(nextShipment);
            setSteps(buildShipmentTimeline(nextShipment));
            return;
          }
        } catch {
          // use fallback/default timeline
        }

        if (cancelled || application) return;

        try {
          const res = await apiClient.get('/beta/my-applications');
          if (cancelled) return;
          if (res.success && Array.isArray(res.data)) {
            const matching = res.data.find((app: { programId?: string }) => app.programId === shipmentKey);
            const nextShipment = matching?.shipment ?? null;
            setShipment(nextShipment);
            setSteps(buildShipmentTimeline(nextShipment));
          }
        } catch {
          // use fallback/default timeline
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadShipment();
    return () => {
      cancelled = true;
    };
  }, [application, programId]);

  if (loading) {
    return (
      <div className="shipment-tracker">
        <h3>📦 Shipment Status</h3>
        <Skeleton variant="text" count={4} width="70%" />
      </div>
    );
  }

  const trackingNumber = shipment?.trackingNumber ? String(shipment.trackingNumber) : '';
  const trackingUrl = shipment?.trackingUrl ? String(shipment.trackingUrl) : '';
  const carrier = String(shipment?.carrier ?? 'Carrier').toUpperCase();
  const estimatedDelivery = shipment?.estimatedDelivery ? String(shipment.estimatedDelivery) : '';

  return (
    <div className="shipment-tracker">
      <h3>📦 Shipment Status</h3>
      <div className="tracker-steps">
        {steps.map((step, i) => (
          <div key={i} className={`tracker-step ${step.done ? 'done' : ''} ${step.current ? 'current' : ''}`}>
            <div className="tracker-dot" />
            <div className="tracker-info">
              <span className="tracker-label">{step.label}</span>
              {step.date && <span className="tracker-date">{step.date}</span>}
            </div>
            {i < steps.length - 1 && <div className="tracker-line" />}
          </div>
        ))}
      </div>
      {trackingNumber && (
        <div className="tracker-meta">
          <span>{carrier}</span>
          {trackingUrl ? (
            <a href={trackingUrl} target="_blank" rel="noreferrer">{trackingNumber}</a>
          ) : (
            <span>{trackingNumber}</span>
          )}
        </div>
      )}
      <p className="tracker-note">
        {estimatedDelivery
          ? `Estimated delivery ${estimatedDelivery}`
          : "You'll get an SMS and email when your kit arrives."}
      </p>
    </div>
  );
}
